import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cloud, FolderTree, CheckCircle2, Link2, Unlink, Copy, Save } from "lucide-react";

function CopyRow({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="flex items-center gap-2">
        <code className="mono text-xs bg-white border border-slate-200 rounded px-2 py-1 flex-1 break-all">{value}</code>
        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Kopiert"); }}><Copy size={13} /></Button>
      </div>
    </div>
  );
}

export default function CloudSettings() {
  const [provider, setProvider] = useState("");
  const [baseFolder, setBaseFolder] = useState("");
  const [shareRole, setShareRole] = useState("commenter");
  const [publicUrl, setPublicUrl] = useState("");
  const [autoFolder, setAutoFolder] = useState(true);
  const [g, setG] = useState({ client_id: "", api_key: "", has_secret: false, redirect_uri: "", origin: "" });
  const [gSecret, setGSecret] = useState("");
  const [gStatus, setGStatus] = useState({ configured: false, connected: false, email: "", connected_at: "" });
  const [p, setP] = useState({ client_id: "", has_secret: false, region: "EU", redirect_uri: "" });
  const [pSecret, setPSecret] = useState("");
  const [pStatus, setPStatus] = useState({ configured: false, connected: false, email: "", region: "EU", connected_at: "" });

  const loadAll = useCallback(async () => {
    try {
      const [s, gc, gs, pc, ps] = await Promise.all([
        api.get("/settings"), api.get("/drive/config"), api.get("/drive/status"),
        api.get("/pcloud/config"), api.get("/pcloud/status"),
      ]);
      setProvider(s.data.storage_provider || "");
      setBaseFolder(s.data.drive_base_folder_id || "");
      setPublicUrl(s.data.public_base_url || "");
      setAutoFolder(s.data.auto_article_folder !== false);
      setShareRole(s.data.google_share_role || "commenter");
      setG(gc.data); setGStatus(gs.data);
      setP(pc.data); setPStatus(ps.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const d = q.get("drive"), pc = q.get("pcloud");
    if (d === "connected") toast.success("Google Drive verbunden");
    else if (d === "norefresh") toast.error("Kein Zugriffstoken erhalten. Bitte Google-Verbindung trennen und erneut verbinden – dabei alle Berechtigungen erlauben.");
    else if (d === "scope_error") toast.error("Google-Drive-Zugriff wurde nicht erteilt. Bitte erneut verbinden und auf der Google-Seite „Alle auswählen“ anhaken, dann „Weiter“.", { duration: 12000 });
    else if (d) toast.error("Google-Verbindung fehlgeschlagen");
    if (pc === "connected") toast.success("pCloud verbunden");
    else if (pc) toast.error("pCloud-Verbindung fehlgeschlagen");
    if (d || pc) window.history.replaceState({}, "", "/einstellungen/cloud");
  }, []);

  const chooseProvider = async (val) => {
    setProvider(val);
    try { await api.put("/settings", { storage_provider: val }); toast.success("Aktiver Anbieter gespeichert"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleAutoFolder = async (val) => {
    setAutoFolder(val);
    try { await api.put("/settings", { auto_article_folder: val }); toast.success(val ? "Ordner werden automatisch erstellt" : "Ordner werden nur noch per Hand erstellt"); }
    catch (e) { setAutoFolder(!val); toast.error(apiError(e.response?.data?.detail)); }
  };

  const savePublicUrl = async () => {
    try { await api.put("/settings", { public_base_url: publicUrl.trim() }); toast.success("Basis-URL gespeichert"); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveGoogle = async () => {
    try {
      await api.put("/drive/config", { client_id: g.client_id, client_secret: gSecret, api_key: g.api_key });
      await api.put("/settings", { drive_base_folder_id: baseFolder, google_share_role: shareRole });
      setGSecret(""); toast.success("Google-Zugangsdaten gespeichert"); loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const connectGoogle = async () => { try { const { data } = await api.get("/drive/connect"); window.location.href = data.authorization_url; } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const disconnectGoogle = async () => { if (!window.confirm("Google-Verbindung trennen?")) return; await api.post("/drive/disconnect"); toast.success("Getrennt"); loadAll(); };

  const savePcloud = async () => {
    try { await api.put("/pcloud/config", { client_id: p.client_id, client_secret: pSecret, region: p.region }); setPSecret(""); toast.success("pCloud-Zugangsdaten gespeichert"); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const connectPcloud = async () => { try { const { data } = await api.get("/pcloud/connect"); window.location.href = data.authorization_url; } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const disconnectPcloud = async () => { if (!window.confirm("pCloud-Verbindung trennen?")) return; await api.post("/pcloud/disconnect"); toast.success("Getrennt"); loadAll(); };

  const ProviderBtn = ({ val, label }) => (
    <button onClick={() => chooseProvider(val)} data-testid={`provider-${val || "none"}`}
      className={`flex-1 rounded-lg border px-4 py-3 text-sm font-semibold transition ${provider === val ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
      {label}
    </button>
  );

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-11 w-11 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600"><Cloud size={22} /></div>
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Cloud-Speicher</h1>
          <p className="text-slate-500 text-sm">Wähle, über welche Cloud die App Ordner &amp; Dateien verwaltet.</p>
        </div>
      </div>

      <div className="mt-5">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Aktiver Anbieter</Label>
        <div className="flex gap-2 mt-2">
          <ProviderBtn val="" label="Keiner" />
          <ProviderBtn val="google" label="Google Drive" />
          <ProviderBtn val="pcloud" label="pCloud (EU)" />
        </div>
        <p className="text-xs text-slate-400 mt-2">Kommentare werden immer in der App erfasst – unabhängig vom gewählten Anbieter.</p>
      </div>

      <label className="mt-5 flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-4 cursor-pointer" data-testid="auto-folder-card">
        <input type="checkbox" checked={autoFolder} onChange={(e) => toggleAutoFolder(e.target.checked)} className="mt-1 h-4 w-4" data-testid="auto-folder-toggle" />
        <span>
          <span className="block text-sm font-semibold text-slate-800">Ordner beim Anlegen eines Artikels automatisch erstellen</span>
          <span className="block text-xs text-slate-500 mt-0.5">Aus: Der Ordner wird erst erstellt, wenn Du im Artikel auf „Ordner erstellen &amp; verknüpfen“ klickst. Empfohlen, solange Google Drive noch im Testmodus ist.</span>
        </span>
      </label>

      <div className="mt-5 bg-white border border-slate-200 rounded-xl p-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Öffentliche Basis-URL (optional)</Label>
        <p className="text-xs text-slate-400 mt-1 mb-2">Für die OAuth-Redirect-URIs. Leer = automatisch aus der aufgerufenen Domain. Nach Veröffentlichung hier ggf. deine Produktions-Domain eintragen (z.&nbsp;B. https://magazin.rot-weiss-berlin.org).</p>
        <div className="flex gap-2">
          <Input value={publicUrl} onChange={(e) => setPublicUrl(e.target.value)} placeholder="https://…" className="mono" data-testid="public-base-url" />
          <Button onClick={savePublicUrl} className="bg-[#0B132B] hover:bg-[#1C2541]"><Save size={15} className="mr-1.5" /> Speichern</Button>
        </div>
      </div>

      {/* pCloud */}
      <div className={`mt-6 rounded-xl border p-5 ${provider === "pcloud" ? "border-sky-300 bg-sky-50/40" : "border-slate-200 bg-white"}`}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><Cloud size={16} /> pCloud (EU)</h2>
          {pStatus.connected
            ? <span className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1"><CheckCircle2 size={14} /> Verbunden</span>
            : pStatus.configured ? <span className="text-xs text-slate-400">Konfiguriert</span> : <span className="text-xs text-slate-400">Nicht konfiguriert</span>}
        </div>
        <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-3 mb-4">
          <p className="text-xs text-slate-700 mb-2">Lege in <a className="text-blue-600 underline" href="https://docs.pcloud.com/my_apps/" target="_blank" rel="noreferrer">pCloud „My applications"</a> eine App an und trage diese Redirect-URI ein:</p>
          <CopyRow label="Redirect-URI" value={p.redirect_uri} />
        </div>
        <div className="space-y-3">
          <div><Label>App Key (Client-ID)</Label><Input value={p.client_id} onChange={(e) => setP({ ...p, client_id: e.target.value })} className="mt-1 mono" data-testid="pcloud-client-id" /></div>
          <div><Label>App Secret (Client-Secret) {p.has_secret && <span className="text-emerald-600 font-normal">(hinterlegt – leer lassen zum Behalten)</span>}</Label><Input type="password" value={pSecret} onChange={(e) => setPSecret(e.target.value)} className="mt-1 mono" placeholder={p.has_secret ? "••••••••" : ""} data-testid="pcloud-client-secret" /></div>
          <div>
            <Label>Rechenzentrum</Label>
            <div className="flex gap-2 mt-1">
              {["EU", "US"].map((r) => (
                <button key={r} onClick={() => setP({ ...p, region: r })} data-testid={`pcloud-region-${r}`}
                  className={`px-3 py-1.5 rounded-md border text-sm ${p.region === r ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white border-slate-200 text-slate-600"}`}>
                  {r === "EU" ? "EU — eapi.pcloud.com" : "US — api.pcloud.com"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={savePcloud} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="pcloud-save"><Save size={15} className="mr-1.5" /> Zugangsdaten speichern</Button>
            {pStatus.connected
              ? <Button variant="outline" onClick={disconnectPcloud} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="pcloud-disconnect"><Unlink size={15} className="mr-1.5" /> Trennen</Button>
              : <Button onClick={connectPcloud} disabled={!pStatus.configured} className="bg-sky-600 hover:bg-sky-700" data-testid="pcloud-connect"><Link2 size={15} className="mr-1.5" /> Mit pCloud verbinden</Button>}
          </div>
          {pStatus.connected && <p className="text-sm text-slate-600">Verbundenes Konto: <b data-testid="pcloud-connected-email">{pStatus.email || "—"}</b>{pStatus.connected_at ? ` · seit ${new Date(pStatus.connected_at).toLocaleString("de-DE")}` : ""}</p>}
        </div>
      </div>

      {/* Google Drive */}
      <div className={`mt-6 rounded-xl border p-5 ${provider === "google" ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><FolderTree size={16} /> Google Drive</h2>
          {gStatus.connected
            ? <span className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1"><CheckCircle2 size={14} /> Verbunden</span>
            : gStatus.expired ? <span className="text-xs text-red-600 font-medium" data-testid="drive-expired">Verbindung abgelaufen – bitte neu verbinden</span>
            : gStatus.configured ? <span className="text-xs text-slate-400">Konfiguriert</span> : <span className="text-xs text-slate-400">Nicht konfiguriert</span>}
        </div>
        <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-3 mb-4 space-y-2">
          <p className="text-xs text-slate-700">In der <a className="text-blue-600 underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a> OAuth-Client (Web) anlegen, Drive API aktivieren, dich als Testnutzer eintragen. Trage ein:</p>
          <CopyRow label="JavaScript-Ursprung" value={g.origin} />
          <CopyRow label="Redirect-URI" value={g.redirect_uri} />
        </div>
        <div className="space-y-3">
          <div><Label>Client-ID</Label><Input value={g.client_id} onChange={(e) => setG({ ...g, client_id: e.target.value })} className="mt-1 mono" placeholder="…apps.googleusercontent.com" data-testid="drive-client-id" /></div>
          <div><Label>Client-Secret {g.has_secret && <span className="text-emerald-600 font-normal">(hinterlegt – leer lassen zum Behalten)</span>}</Label><Input type="password" value={gSecret} onChange={(e) => setGSecret(e.target.value)} className="mt-1 mono" placeholder={g.has_secret ? "••••••••" : "GOCSPX-…"} data-testid="drive-client-secret" /></div>
          <div><Label>Basis-Ordner-ID (optional)</Label><Input value={baseFolder} onChange={(e) => setBaseFolder(e.target.value)} className="mt-1 mono" data-testid="drive-base-folder" /></div>
          <div>
            <Label>Zugriff fürs Team (Freigabe je Ordner)</Label>
            <div className="flex gap-2 mt-1">
              {[["reader", "Ansehen"], ["commenter", "Kommentieren"], ["writer", "Bearbeiten"]].map(([val, lbl]) => (
                <button key={val} onClick={() => setShareRole(val)} data-testid={`drive-role-${val}`}
                  className={`px-3 py-1.5 rounded-md border text-sm ${shareRole === val ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white border-slate-200 text-slate-600"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">Fürs Korrekturlesen: „Kommentieren" (Vorschläge/Notizen) oder „Bearbeiten" (direkt im Dokument). Gilt für neu angelegte Ordner.</p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={saveGoogle} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="drive-save-config"><Save size={15} className="mr-1.5" /> Zugangsdaten speichern</Button>
            {gStatus.connected
              ? <Button variant="outline" onClick={disconnectGoogle} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="drive-disconnect"><Unlink size={15} className="mr-1.5" /> Trennen</Button>
              : <Button onClick={connectGoogle} disabled={!gStatus.configured} className="bg-emerald-600 hover:bg-emerald-700" data-testid="drive-connect"><Link2 size={15} className="mr-1.5" /> Mit Google Drive verbinden</Button>}
          </div>
          {gStatus.connected && <p className="text-sm text-slate-600">Verbundenes Konto: <b data-testid="drive-connected-email">{gStatus.email || "—"}</b>{gStatus.connected_at ? ` · seit ${new Date(gStatus.connected_at).toLocaleString("de-DE")}` : ""}</p>}
        </div>
      </div>
    </div>
  );
}
